import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { FournisseurService } from "../../services/fournisseurs/fournisseurService";
import { DashboardCharts } from "../../components/analytics/DashboardCharts";
import { TrendingUp, FileText, CreditCard, Activity } from "lucide-react";
import { toast } from "react-hot-toast";

export const FournisseurAnalytics: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (user?.profile?.id) {
        try {
          const response = await FournisseurService.getFournisseurStats(user.profile.id);
          setStats(response.data);
        } catch (error) {
          console.error("Error fetching stats", error);
          toast.error("Erreur chargement statistiques");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  const metrics = [
    {
      title: "Factures Totales",
      value: stats?.factures_totales || "0",
      change: "+0%",
      trend: "neutral",
      icon: FileText,
      color: "blue",
    },
    {
      title: "Montant Total",
      value: stats?.montant_total 
        ? new Intl.NumberFormat("fr-FR", { style: "currency", currency: "XOF" }).format(stats.montant_total)
        : "0 FCFA",
      change: "+0%",
      trend: "up",
      icon: TrendingUp,
      color: "green",
    },
    {
      title: "Paiements Reçus",
      value: stats?.paiements_recus?.count || "0",
      change: "+0%",
      trend: "up",
      icon: CreditCard,
      color: "purple",
    },
    {
      title: "Taux de Performance",
      value: "100%", 
      change: "+0%",
      trend: "neutral",
      icon: Activity,
      color: "orange",
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytique Fournisseur</h1>
          <p className="mt-1 text-sm text-gray-600">
            Performance financière et activité détaillée
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                      <Icon className={`h-6 w-6 text-${metric.color}-600`} />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {metric.title}
                      </dt>
                      <dd>
                        <div className="text-2xl font-semibold text-gray-900">
                          {metric.value}
                        </div>
                        <div className={`text-sm font-medium ${
                            metric.trend === 'up' ? 'text-success-600' : 'text-gray-500'
                        }`}>
                          {metric.change}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
          Évolution des Factures et Paiements
        </h3>
        <DashboardCharts />
      </div>
    </div>
  );
};
